/**
 * useBooking Hook
 *
 * Manages booking state, submission, and fetching via Cloud Functions and Firestore.
 */

import { useState, useCallback, useEffect } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../../services/firebase';
import { useAuth } from '../../../contexts';

export interface GuestCount {
  adults: number;
  children: number;
  infants: number;
}

export interface BookingFormData {
  listingId: string;
  checkIn: Date;
  checkOut: Date;
  guests: GuestCount;
  specialRequests?: string;
  paymentMethod: 'stripe' | 'whish_money';
  acknowledgedPolicy: boolean;
  guestPhone?: string;
  checkInTime?: string;
  tripPurpose?: string;
  promoCode?: string;
}

export interface BookingData {
  id: string;
  listingId: string;
  guestId: string;
  hostId: string;
  checkIn: Date;
  checkOut: Date;
  nights: number;
  guests: GuestCount;
  pricing: {
    nightlyRate: number;
    nightlyTotal: number;
    cleaningFee: number;
    platformCommission: number;
    taxes: number;
    securityDeposit: number;
    totalAmount: number;
    hostPayout: number;
    currency: string;
  };
  status: 'pending' | 'confirmed' | 'cancelled' | 'completed';
  paymentStatus: 'pending' | 'partial' | 'paid' | 'refunded';
  paymentMethod: 'stripe' | 'whish_money';
  specialRequests?: string;
  listing?: {
    title: string;
    photo: string;
    location: string;
  };
  createdAt: Date;
  confirmedAt?: Date;
}

// Helper to safely convert Firestore timestamp or date value to Date
const toSafeDate = (value: unknown): Date => {
  if (!value) return new Date();
  if (value instanceof Date) return value;
  if (typeof value === 'object' && 'toDate' in value && typeof (value as { toDate: () => Date }).toDate === 'function') {
    return (value as { toDate: () => Date }).toDate();
  }
  const d = new Date(value as string | number);
  return isNaN(d.getTime()) ? new Date() : d;
};

/**
 * Create a new booking via Cloud Function
 */
export const useCreateBooking = () => {
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const createBooking = useCallback(
    async (
      formData: BookingFormData,
      listingData: {
        hostId: string;
        instantBooking: boolean;
        pricing: {
          nightlyRate: number;
          weekendRate?: number;
          cleaningFee?: number;
          securityDeposit?: number;
        };
        title: string;
        photo: string;
        location: string;
      },
      pricing: {
        nights: number;
        nightlyTotal: number;
        cleaningFee: number;
        platformCommission: number;
        securityDeposit: number;
        totalAmount: number;
        hostPayout: number;
      }
    ): Promise<BookingData> => {
      if (!user) {
        throw new Error('Must be logged in to create a booking');
      }

      setIsLoading(true);
      setError(null);

      try {
        const callable = httpsCallable<
          Record<string, unknown>,
          { bookingId: string; status: string }
        >(functions, 'createBooking');

        const result = await callable({
          listingId: formData.listingId,
          checkIn: formData.checkIn.toISOString(),
          checkOut: formData.checkOut.toISOString(),
          guests: formData.guests,
          specialRequests: formData.specialRequests || null,
          paymentMethod: formData.paymentMethod,
          guestPhone: formData.guestPhone || null,
          checkInTime: formData.checkInTime || null,
          tripPurpose: formData.tripPurpose || null,
          promoCode: formData.promoCode || null,
          pricing: {
            nightlyRate: listingData.pricing.nightlyRate,
            nights: pricing.nights,
            nightlyTotal: pricing.nightlyTotal,
            cleaningFee: pricing.cleaningFee,
            platformCommission: pricing.platformCommission,
            securityDeposit: pricing.securityDeposit,
            totalAmount: pricing.totalAmount,
            hostPayout: pricing.hostPayout,
          },
          listing: {
            title: listingData.title,
            photo: listingData.photo,
            location: listingData.location,
          },
        });

        const { bookingId, status } = result.data;

        // Return the booking data matching the expected interface
        const booking: BookingData = {
          id: bookingId,
          listingId: formData.listingId,
          guestId: user.uid,
          hostId: listingData.hostId,
          checkIn: formData.checkIn,
          checkOut: formData.checkOut,
          nights: pricing.nights,
          guests: formData.guests,
          pricing: {
            nightlyRate: listingData.pricing.nightlyRate,
            nightlyTotal: pricing.nightlyTotal,
            cleaningFee: pricing.cleaningFee,
            platformCommission: pricing.platformCommission,
            taxes: 0,
            securityDeposit: pricing.securityDeposit,
            totalAmount: pricing.totalAmount,
            hostPayout: pricing.hostPayout,
            currency: 'USD',
          },
          status: status as BookingData['status'],
          paymentStatus: 'pending',
          paymentMethod: formData.paymentMethod,
          specialRequests: formData.specialRequests,
          listing: {
            title: listingData.title,
            photo: listingData.photo,
            location: listingData.location,
          },
          createdAt: new Date(),
          confirmedAt: status === 'confirmed' ? new Date() : undefined,
        };

        return booking;
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Booking failed';
        setError(message);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [user]
  );

  return { createBooking, isLoading, error };
};

/**
 * Fetch a booking by ID from Firestore
 */
export const useBookingDetails = (bookingId: string | undefined) => {
  const [isLoading, setIsLoading] = useState(true);
  const [booking, setBooking] = useState<BookingData | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!bookingId) {
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    async function fetchBooking() {
      try {
        const bookingRef = doc(db, 'bookings', bookingId!);
        const bookingSnap = await getDoc(bookingRef);

        if (cancelled) return;

        if (!bookingSnap.exists()) {
          setError('Booking not found');
          setIsLoading(false);
          return;
        }

        const data = bookingSnap.data();

        const bookingData: BookingData = {
          id: bookingSnap.id,
          listingId: data.listingId,
          guestId: data.guestId,
          hostId: data.hostId,
          checkIn: toSafeDate(data.checkIn),
          checkOut: toSafeDate(data.checkOut),
          nights: data.nights || 1,
          guests: data.guests || { adults: 1, children: 0, infants: 0 },
          pricing: data.pricing || {
            nightlyRate: 0,
            nightlyTotal: 0,
            cleaningFee: 0,
            platformCommission: 0,
            taxes: 0,
            securityDeposit: 0,
            totalAmount: 0,
            hostPayout: 0,
            currency: 'USD',
          },
          status: data.status || 'pending',
          paymentStatus: data.paymentStatus || 'pending',
          paymentMethod: data.paymentMethod || 'stripe',
          specialRequests: data.specialRequests || undefined,
          listing: data.listing || undefined,
          createdAt: toSafeDate(data.createdAt),
          confirmedAt: data.confirmedAt
            ? toSafeDate(data.confirmedAt)
            : undefined,
        };

        setBooking(bookingData);
      } catch (err) {
        if (!cancelled) {
          console.error('Error fetching booking:', err);
          setError(
            err instanceof Error ? err.message : 'Failed to fetch booking'
          );
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    }

    fetchBooking();

    return () => {
      cancelled = true;
    };
  }, [bookingId]);

  return { booking, isLoading, error };
};

export default useCreateBooking;
